import { Component, type ErrorInfo, type ReactNode } from 'react'

interface Props {
  children: ReactNode
}

interface State {
  error: Error | null
}

/**
 * Rede de segurança global: em vez de uma tela branca, o usuário vê o que
 * houve e um caminho de saída (heurística #9 de Nielsen).
 */
export class AppErrorBoundary extends Component<Props, State> {
  state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('[AppErrorBoundary]', error, info.componentStack)
  }

  private handleReload = () => {
    window.location.reload()
  }

  render() {
    const { error } = this.state
    if (!error) return this.props.children

    return (
      <div
        role="alert"
        className="flex min-h-screen items-center justify-center bg-[var(--surface-app)] p-6"
      >
        <div className="sh-card w-full max-w-md p-8 text-center">
          <div className="mx-auto mb-4 flex size-12 items-center justify-center rounded-full bg-[var(--danger-soft)] text-2xl">
            ⚠️
          </div>
          <h1 className="text-xl font-semibold text-[var(--text-primary)]">
            Algo saiu do esperado
          </h1>
          <p className="mt-2 text-sm text-[var(--text-secondary)]">
            A tela não pôde ser carregada. Nada do seu trabalho salvo foi perdido — recarregue para
            continuar.
          </p>
          <button
            type="button"
            onClick={this.handleReload}
            className="mt-6 inline-flex h-10 w-full items-center justify-center rounded-lg bg-[var(--accent)] px-4 text-sm font-semibold text-[var(--accent-contrast)] transition hover:opacity-90"
          >
            Recarregar aplicativo
          </button>
          <details className="mt-4 text-left">
            <summary className="cursor-pointer text-xs text-[var(--text-muted)]">
              Detalhes técnicos
            </summary>
            <pre className="sh-scroll-thin mt-2 max-h-40 overflow-auto rounded-md bg-[var(--surface-inset)] p-3 text-xs text-[var(--text-secondary)]">
              {error.message}
            </pre>
          </details>
        </div>
      </div>
    )
  }
}
